<template>
  <SoDialog v-model="value" card-title="Reading history" :max-width="1250">
    <SoTabs v-model="currentTab" class="pb-2">
      <template v-for="[register, meterpoint] in registers" :key="register.id">
        <SoTab
          :ref="`reading-history-register-${register.id}`"
          :title="getTabTitle(register, meterpoint)"
          :icon="meterpoint.isElectricity ? 'electric' : 'gas'"
          :icon-color="meterpoint.isElectricity ? 'electric' : 'gas'"
          eager
        >
          <ReadingHistoryRegister :register="register" />
        </SoTab>
      </template>
    </SoTabs>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import ReadingHistoryRegister from "../components/ReadingHistoryRegister.vue"
  import SoTabs from "@/components/tabs/SoTabs.vue"
  import SoTab from "@/components/tabs/SoTab.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IRegister } from "@/models/meterRegister/meterRegister.types"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { queryClient } from "@/api/utils/useQuery"
  import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
  import { DEFAULT_POLLING_TIME } from "@/constants/general"

  export default defineComponent({
    components: { SoDialog, ReadingHistoryRegister, SoTab, SoTabs },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      initialTabIdentifier: {
        type: String,
        default: "",
        required: false,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        currentTab: 0,
        pollingInterval: null as null | ReturnType<typeof setInterval>,
      }
    },
    computed: {
      registers() {
        return this.current.meterpoints.data.reduce<[IRegister, IMeterpoint][]>(
          (registers, meterpoint) => [
            ...registers,
            ...meterpoint.registers.map(
              (register) => [register, meterpoint] as [IRegister, IMeterpoint],
            ),
          ],
          [],
        )
      },
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
    watch: {
      initialTabIdentifier: {
        handler(newVal) {
          if (newVal) {
            this.setInitialTab()
          }
        },
        immediate: true,
      },
      // The modal component stays mounted while hidden, so the readings poll
      // only runs while the dialog is open — matching the old per-register
      // poll that lived inside the dialog content.
      modelValue: {
        handler(isOpen: boolean) {
          if (isOpen) {
            this.startPolling()
          } else {
            this.stopPolling()
          }
        },
        immediate: true,
      },
    },
    beforeUnmount() {
      this.stopPolling()
    },
    methods: {
      startPolling() {
        if (this.pollingInterval) return
        this.pollingInterval = setInterval(
          () => this.fetchReadings(),
          DEFAULT_POLLING_TIME,
        )
      },
      stopPolling() {
        if (this.pollingInterval) {
          clearInterval(this.pollingInterval)
          this.pollingInterval = null
        }
      },
      fetchReadings() {
        const meterpointsQueryKey =
          BackofficeRestQueries.meterpoints.forAccount(
            this.current.accountNumber,
          ).queryKey

        queryClient.invalidateQueries({ queryKey: meterpointsQueryKey })
      },
      getTabTitle(register, meterpoint) {
        const title = `${meterpoint.meterpointType}${
          meterpoint.isExport ? " - EXPORT" : ""
        } - ${meterpoint.identifier}`

        return !!register.rateName && register.rateName !== "Standard"
          ? `${title} (${register.rateName})`
          : title
      },
      setInitialTab() {
        const index = this.registers.findIndex(
          ([, meterpoint]) =>
            meterpoint.identifier === this.initialTabIdentifier,
        )
        this.currentTab = index !== -1 ? index : 0
      },
    },
  })
</script>
